/**
 * Decision Room → Forms section, PROSPECT side (2026-09-07, flag `forms`).
 *
 * Lists the forms the seller embedded in this room and lets the buyer
 * answer them in place. Reads/writes go through the room's public
 * routes (routes/buyerSpacesPublic.js /:slug/forms…) on the room
 * credential — same token, same rate limits, same honeypot as every
 * other buyer write in the room.
 *
 * Light-locked, inline styles: this file is imported by
 * BuyerSpaceProspectView and follows its conventions (no --room-*
 * tokens on a buyer surface; those follow the SELLER's theme).
 *
 * The parent renders the section hero; this panel owns the cards.
 */

import { useEffect, useState } from 'react';
import FormFill, { validateRequired, answersForSubmit } from './FormFill';
import { API_BASE, formatDate } from '../utils/apiRequest';

export default function RoomFormsProspectPanel({ slug, token, accent, sellerCompanyName }) {
  const [forms, setForms] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/forms?t=${encodeURIComponent(token)}`);
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Failed to load (status ${r.status})`);
      setForms(body.forms || []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  if (error) return <Card><p style={{ margin: 0, color: '#dc2626' }}>{error}</p></Card>;
  if (!forms) return <Card><p style={{ margin: 0, color: '#94a3b8' }}>Loading…</p></Card>;
  if (forms.length === 0) return <Card><p style={{ margin: 0, color: '#94a3b8' }}>Nothing to fill in right now.</p></Card>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {forms.map((f) => (
        <ProspectFormCard key={f.id} form={f} slug={slug} token={token} accent={accent} sellerCompanyName={sellerCompanyName} />
      ))}
    </div>
  );
}

function ProspectFormCard({ form, slug, token, accent, sellerCompanyName }) {
  const [values, setValues] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const [open, setOpen] = useState(!form.alreadySubmittedAt);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [doneAt, setDoneAt] = useState(null);

  const submittedAt = doneAt || form.alreadySubmittedAt;

  const submit = async (e) => {
    e.preventDefault();
    const missing = validateRequired(form.fields, values);
    if (missing) { setSubmitError(missing); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch(
        `${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/forms/${encodeURIComponent(form.id)}/responses?t=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: answersForSubmit(form.fields, values), website_url: honeypot }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Couldn't send (status ${r.status})`);
      setDoneAt(new Date().toISOString());
      setOpen(false);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{form.name}</h2>
          {form.description && <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#475569' }}>{form.description}</p>}
        </div>
        {submittedAt && (
          <span style={{ flex: 'none', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
            Sent {formatDate(submittedAt)}
          </span>
        )}
      </div>

      {!open ? (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, color: '#334155' }}>
            {doneAt ? `Thank you — sent to ${sellerCompanyName || 'the seller'}.` : 'You already answered this.'}
          </span>
          <button type="button" onClick={() => setOpen(true)} style={ghost(accent)}>
            {doneAt ? 'Edit answers' : 'Update my answers'}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate style={{ marginTop: 18 }}>
          <FormFill fields={form.fields} values={values} onChange={(id, v) => setValues((p) => ({ ...p, [id]: v }))} accent={accent} tone="light" disabled={submitting} />
          <div aria-hidden="true" style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
            <label>Website<input type="text" name="website_url" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></label>
          </div>
          {submitError && (
            <div role="alert" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14 }}>{submitError}</div>
          )}
          <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="submit" disabled={submitting} style={solid(accent)}>{submitting ? 'Sending…' : (submittedAt ? 'Send updated answers' : 'Send answers')}</button>
            {submittedAt && <button type="button" onClick={() => setOpen(false)} style={ghost(accent)}>Cancel</button>}
          </div>
        </form>
      )}
    </Card>
  );
}

function Card({ children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, position: 'relative' }}>
      {children}
    </div>
  );
}
const solid = (accent) => ({ padding: '10px 16px', borderRadius: 10, border: `1px solid ${accent}`, background: accent, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' });
const ghost = (accent) => ({ padding: '8px 14px', borderRadius: 10, border: `1px solid ${accent}`, background: '#fff', color: accent, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' });
